import { keyframes, styled } from "goober";
import type { ReactElement, ReactNode } from "react";
import { formatRelativeTime } from "../lib/formatRelativeTime";
import { Icon } from "./Icon";
import { MetaSep } from "./MetaSeparator";
import { variantStyles } from "./Text";
import { Tooltip } from "./Tooltip";

const Container = styled("div")`
  display: flex;
  align-items: center;
  gap: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-left: 3px;
  margin-left: -3px;
`;

const pulseRing = keyframes`
  0% {
    opacity: 0.8;
    transform: translate(-50%, -50%) scale(0.5);
  }
  66% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(1);
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(1);
  }
`;

const LivePulseDot = styled("span")`
  position: relative;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--green-600);
  flex-shrink: 0;

  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: var(--green-600);
    animation: ${pulseRing} 1.5s cubic-bezier(0, 0, 0.2, 1) infinite;
  }
`;

export function PulseDot({ paused = false }: { paused?: boolean } = {}) {
  if (paused) {
    return <Icon name="circle-pause" size={10} color="var(--text-secondary)" />;
  }
  return <LivePulseDot />;
}

const Stat = styled("span")`
  display: flex;
  align-items: center;
  gap: 4px;
`;

type Props = {
  workflow: {
    updatedAt: number;
    autofixBranchId: string | null;
    autoImproveBranchId: string | null;
    pausedAt: number | null;
    branches: readonly { name: string }[] | undefined;
  };
  connectedAppName?: string | null;
};

export function WorkflowSubtext({ workflow, connectedAppName }: Props) {
  const branches = workflow.branches ?? [];
  const isLive = branches.some((b) => b.name === "main");
  const isPaused = workflow.pausedAt != null;
  const branchCount = branches.filter((b) => b.name !== "main").length;
  const hasAutofix = workflow.autofixBranchId != null;
  const hasAutoImprove = workflow.autoImproveBranchId != null;
  const hasConnector = !!connectedAppName;

  const style = variantStyles["text-small"];

  const stats: ReactElement[] = [];
  if (isLive) {
    stats.push(
      <Stat key="live">
        <PulseDot paused={isPaused} />
        {isPaused ? "Paused" : "Live"}
      </Stat>
    );
  }
  if (isLive && hasConnector) {
    stats.push(
      <Tooltip key="connector" content={connectedAppName}>
        <Stat>
          <Icon name="api-key-custom" size={10} />
          Connector
        </Stat>
      </Tooltip>
    );
  }
  if (hasAutofix) {
    stats.push(
      <Stat key="autofix">
        <Icon name="radar" size={10} />1 autofix
      </Stat>
    );
  }
  if (hasAutoImprove) {
    stats.push(
      <Stat key="autotune">
        <Icon name="audio-waveform" size={10} />1 autotune
      </Stat>
    );
  }
  if (branchCount > 0) {
    stats.push(
      <Stat key="branches">
        <Icon name="git-branch" size={10} />
        {branchCount} {branchCount === 1 ? "branch" : "branches"}
      </Stat>
    );
  }

  const parts: ReactNode[] = [];
  stats.forEach((stat, i) => {
    if (i > 0) parts.push(<MetaSep key={`sep-${stat.key}`} />);
    parts.push(stat);
  });
  if (stats.length > 0) parts.push(<MetaSep key="sep-end" />);

  return (
    <Container
      style={{
        color: "var(--text-secondary)",
        fontFamily: style.fontFamily,
        fontWeight: style.fontWeight,
        fontSize: style.fontSize,
        lineHeight: style.lineHeight,
        letterSpacing: style.letterSpacing,
      }}
    >
      {parts}
      <span>Last updated {formatRelativeTime(workflow.updatedAt)}</span>
    </Container>
  );
}
